/**
 * Form Filling Protected Route
 * 
 * Protects the form filling page so only the user for whom the link
 * was created can access it. Requires Auth0 authentication and verifies
 * that the logged-in user's email matches the session's userEmail.
 * 
 * This is a simpler version for the User Platform (no onboarding checks).
 */

import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import LogoLoader from '../components/LogoLoader';

const API_BASE = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 50%, #f9fbff 100%);
  padding: 20px;
  box-sizing: border-box;
`;

const Container = styled.div`
  max-width: 480px;
  width: 100%;
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;
`;

const LoaderSlot = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto 12px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #192947;
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #455776;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dce5f4;
  border-radius: 16px;
  padding: 32px;
  backdrop-filter: blur(10px);
`;

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 32px;
  
  background: ${props => props.$type === 'error' 
    ? 'rgba(229, 72, 77, 0.15)' 
    : props.$type === 'warning'
    ? 'rgba(239, 155, 97, 0.15)'
    : 'rgba(91, 97, 234, 0.15)'};
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #5b61ea 0%, #885de8 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 24px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(91, 97, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const InfoBox = styled.div`
  background: rgba(91, 97, 234, 0.1);
  border: 1px solid rgba(91, 97, 234, 0.2);
  border-radius: 10px;
  padding: 16px;
  margin-top: 20px;
  text-align: left;
`;

const InfoLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #65748d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #5874be;
  word-break: break-all;
`;

// ============================================
// COMPONENT
// ============================================

const FormFillingProtectedRoute = ({ children }) => {
  const { sessionId } = useParams();
  const location = useLocation();
  const { 
    isAuthenticated, 
    isLoading: authLoading, 
    loginWithRedirect, 
    user, 
    getAccessTokenSilently,
    logout 
  } = useAuth0();
  
  const [status, setStatus] = useState('checking'); // 'checking' | 'authorized' | 'unauthorized' | 'error' | 'expired'
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Store the form URL for return after login
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Store the URL to return to after login
      const returnToUrl = location.pathname + location.search;
      localStorage.setItem('form_filling_return_url', returnToUrl);
    }
  }, [authLoading, isAuthenticated, location]);
  
  // Redirect to Auth0 login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Redirect to login
      loginWithRedirect({
        appState: {
          returnTo: location.pathname + location.search
        }
      });
    }
  }, [authLoading, isAuthenticated, loginWithRedirect, location]);
  
  // Verify user access to the session
  useEffect(() => {
    const verifyAccess = async () => {
      if (!isAuthenticated || !user || !sessionId) return;
      
      try {
        // Get access token
        const token = await getAccessTokenSilently();
        
        // Call backend to verify access
        const response = await axios.get(
          `${API_BASE}/form-filling/sessions/${sessionId}/verify-access`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.data.success && response.data.authorized) {
          setSessionInfo(response.data.session);
          setStatus('authorized');
          // Clear the stored return URL
          localStorage.removeItem('form_filling_return_url');
        } else {
          setSessionInfo(response.data.session);
          setError(response.data.error || 'Access denied');
          setStatus('unauthorized');
        }
      } catch (err) {
        console.error('[FormFillingProtected] Error verifying access:', err);
        
        if (err.response?.status === 410) {
          setStatus('expired');
        } else if (err.response?.status === 403) {
          setSessionInfo(err.response?.data?.session);
          setError(err.response?.data?.error || 'You are not authorized to access this form');
          setStatus('unauthorized');
        } else if (err.response?.status === 404) {
          setError('Form session not found');
          setStatus('error');
        } else {
          setError(err.response?.data?.error || 'Failed to verify access');
          setStatus('error');
        }
      }
    };
    
    if (isAuthenticated && user) {
      verifyAccess();
    }
  }, [isAuthenticated, user, sessionId, getAccessTokenSilently]);
  
  // Handle logout and login as different user
  const handleLoginAsDifferent = () => {
    logout({
      logoutParams: {
        returnTo: window.location.href
      }
    });
  };
  
  // Loading state (Auth0 loading or verifying access)
  if (authLoading || (isAuthenticated && status === 'checking')) {
    return (
      <PageWrapper>
        <Container>
          <Card>
            <LoaderSlot><LogoLoader size={150} message="Verifying your access" /></LoaderSlot>
            <Subtitle>
              Please wait while we verify your credentials...
            </Subtitle>
          </Card>
        </Container>
      </PageWrapper>
    );
  }
  
  // Not authenticated - will redirect to login
  if (!isAuthenticated) {
    return (
      <PageWrapper>
        <Container>
          <Card>
            <LoaderSlot><LogoLoader size={150} message="Redirecting to sign in" /></LoaderSlot>
            <Subtitle>
              Please sign in to access this form...
            </Subtitle>
          </Card>
        </Container>
      </PageWrapper>
    );
  }
  
  // Session expired
  if (status === 'expired') {
    return (
      <PageWrapper>
        <Container>
          <Card>
            <IconWrapper $type="warning">⏰</IconWrapper>
            <Title>Link Expired</Title>
            <Subtitle>
              This form filling link has expired. Please contact your advisor to get a new link.
            </Subtitle>
          </Card>
        </Container>
      </PageWrapper>
    );
  }
  
  // Unauthorized - user email doesn't match session
  if (status === 'unauthorized') {
    return (
      <PageWrapper>
        <Container>
          <Card>
            <IconWrapper $type="error">🔒</IconWrapper>
            <Title>Access Denied</Title>
            <Subtitle>
              This form was created for a different user. You can only access forms that were specifically created for your account.
            </Subtitle>
            
            <InfoBox>
              <InfoLabel>You are signed in as</InfoLabel>
              <InfoValue>{user?.email}</InfoValue>
            </InfoBox>
            
            {sessionInfo?.userEmail && (
              <InfoBox style={{ marginTop: '12px' }}>
                <InfoLabel>This form is for</InfoLabel>
                <InfoValue>
                  {sessionInfo.userEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3')}
                </InfoValue>
              </InfoBox>
            )}
            
            <div style={{ marginTop: '24px' }}>
              <Button onClick={handleLoginAsDifferent}>
                Sign in with Different Account
              </Button>
            </div>
          </Card>
        </Container>
      </PageWrapper>
    );
  }
  
  // Error state
  if (status === 'error') {
    return (
      <PageWrapper>
        <Container>
          <Card>
            <IconWrapper $type="error">❌</IconWrapper>
            <Title>Something Went Wrong</Title>
            <Subtitle>
              {error || 'Unable to load the form. Please try again later.'}
            </Subtitle>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </Card>
        </Container>
      </PageWrapper>
    );
  }
  
  // Authorized - render the form filling page
  if (status === 'authorized') {
    return children;
  }
  
  // Fallback loading
  return (
    <PageWrapper>
      <Container>
        <Card>
          <LoaderSlot><LogoLoader size={150} message="Opening your form" /></LoaderSlot>
        </Card>
      </Container>
    </PageWrapper>
  );
};

export default FormFillingProtectedRoute;
